import type { DiaClient } from './client';
import { browserOffline } from './queries';
import { cacheKey, getDb } from './dexie';

/**
 * Power, as the phones see it.
 *
 * Every number on the Energia screen was decided by the ingest function
 * (supabase/functions/_shared/logic/power.ts) and stored as one JSON document.
 * A phone only reads it, keeps a copy for the dead spots, and works out how old
 * each value is by its own clock, corrected for the difference from the
 * server's. The types come straight from the server module so the two cannot
 * drift; they are erased at build time, so no server code ships to a phone.
 */

import type {
  AcUnit,
  Freshness,
  GeneratorState,
  HourBucket,
  LoadRow,
  PowerStatus,
  Projection,
  ProjectionReason,
} from '../../../supabase/functions/_shared/logic/power';

export type { AcUnit, Freshness, GeneratorState, HourBucket, LoadRow, PowerStatus, Projection, ProjectionReason };

export type PowerDashboard = {
  status: PowerStatus;
  /** When the server worked this out. */
  computedAt: string;
  /** The server's clock when it answered. */
  serverNow: string;
};

export type PowerView = {
  dashboard: PowerDashboard | null;
  /** True when this is the phone's own copy and the island did not answer. */
  cached: boolean;
  /** When this copy was fetched, on the phone's clock. Null when there is none. */
  fetchedAt: number | null;
  /** Add to Date.now() to get server time. Zero when unknown. */
  skewMs: number;
};

export const STALE_MINUTES = 15;
export const SILENT_MINUTES = 60;

/** Same thresholds as the server (Section 8): 15 minutes stale, an hour silent. */
export function freshnessOf(ageSeconds: number): Freshness {
  if (ageSeconds > SILENT_MINUTES * 60) return 'silent';
  if (ageSeconds > STALE_MINUTES * 60) return 'stale';
  return 'fresh';
}

/** Whole seconds since an ISO timestamp, never negative, on server time. */
export function ageSeconds(ts: string, nowMs: number, skewMs = 0): number {
  return Math.max(0, Math.round((nowMs + skewMs - Date.parse(ts)) / 1000));
}

const POWER_KEY = cacheKey('power', 'dashboard');

type Stored = { dashboard: PowerDashboard; skewMs: number };

async function readStored(): Promise<PowerView> {
  try {
    const hit = await getDb().cache.get(POWER_KEY);
    if (hit) {
      const row = hit.row as Stored;
      return { dashboard: row.dashboard, cached: true, fetchedAt: hit.cachedAt, skewMs: row.skewMs };
    }
  } catch {
    // No local store. Nothing to show, and that is said honestly.
  }
  return { dashboard: null, cached: true, fetchedAt: null, skewMs: 0 };
}

/**
 * The Energia document. Network first, the phone's own copy second, and never a
 * throw for being offline. A copy keeps its original timestamps, so a copy an
 * hour old shows "no signal from the island" on its own.
 */
export async function loadPower(client: DiaClient): Promise<PowerView> {
  if (browserOffline()) return readStored();
  try {
    const { data, error } = await client.rpc('power_dashboard');
    if (error) throw new Error(error.message);
    const dashboard = data as PowerDashboard | null;
    if (!dashboard) return readStored().then((v) => (v.dashboard ? v : { ...v, cached: false }));
    const now = Date.now();
    const skewMs = Date.parse(dashboard.serverNow) - now;
    try {
      await getDb().cache.put({
        key: POWER_KEY,
        entity: 'power',
        id: 'dashboard',
        row: { dashboard, skewMs } satisfies Stored,
        cachedAt: now,
      });
    } catch {
      // Not stored. Fetched again next time.
    }
    return { dashboard, cached: false, fetchedAt: now, skewMs };
  } catch {
    return readStored();
  }
}

/* ------------------------------------------------------------------ */
/* The guest tile                                                      */
/* ------------------------------------------------------------------ */

export type Sunshine = {
  days: number;
  asOf: string;
  /** Server clock minus the phone's at the moment of the read, so the age is the server's. */
  skewMs?: number;
};

const SUNSHINE_KEY = cacheKey('power', 'sunshine');
/**
 * The note is a claim about the present, so it hides at the stale line (15
 * minutes, Section 8), the same moment any value on the ops screens goes grey.
 */
const SUNSHINE_MAX_AGE_MS = STALE_MINUTES * 60_000;

/**
 * A count is only worth showing while it is whole days and recent. Recent is
 * judged on the server's clock (the phone's, corrected by the skew taken when it
 * was read), so a phone with the wrong time cannot keep an old number alive or
 * hide a fresh one.
 */
export function sunshineVisible(s: Sunshine | null, nowMs: number): s is Sunshine {
  if (!s || !Number.isInteger(s.days) || s.days < 1) return false;
  const asOf = Date.parse(s.asOf);
  return Number.isFinite(asOf) && nowMs + (s.skewMs ?? 0) - asOf <= SUNSHINE_MAX_AGE_MS;
}

/**
 * Days since the generator last ran, or null when there is nothing honest to
 * say. Null covers: no data, stale data, the generator running or unknown, and
 * under a day. A copy kept on the phone is used only while it is still recent
 * by its own timestamp, so a guest offline for a day sees no tile, not a
 * yesterday number.
 */
export async function loadSunshine(client: DiaClient): Promise<Sunshine | null> {
  const fromCache = async (): Promise<Sunshine | null> => {
    try {
      const hit = await getDb().cache.get(SUNSHINE_KEY);
      const row = hit?.row as Sunshine | null | undefined;
      return row && sunshineVisible(row, Date.now()) ? row : null;
    } catch {
      return null;
    }
  };

  if (browserOffline()) return fromCache();
  try {
    const { data, error } = await client.rpc('guest_sunshine');
    if (error) throw new Error(error.message);
    const row = Array.isArray(data) ? data[0] : null;
    const serverNow = row && row.server_now ? Date.parse(String(row.server_now)) : Number.NaN;
    const next: Sunshine | null = row
      ? {
          days: Number(row.days),
          asOf: String(row.as_of),
          ...(Number.isFinite(serverNow) ? { skewMs: serverNow - Date.now() } : {}),
        }
      : null;
    try {
      await getDb().cache.put({
        key: SUNSHINE_KEY,
        entity: 'power',
        id: 'sunshine',
        row: next,
        cachedAt: Date.now(),
      });
    } catch {
      // Not stored.
    }
    return next && sunshineVisible(next, Date.now()) ? next : null;
  } catch {
    return fromCache();
  }
}
